import { useEffect } from 'react'
import { getConsent, onConsentChange } from '@/lib/cookie-consent'

// Loads Microsoft Clarity, but only if a project id is actually configured --
// same "not configured is a choice, not a failure" pattern as the optional
// PIXEL_ID/PIXEL_TOKEN in functions/api/lead.js. Without
// VITE_CLARITY_PROJECT_ID set at build time, this silently does nothing,
// which matters because the Privacy Policy says Clarity is in use: it needs
// to actually be true, not aspirational.
//
// Also gated on cookie consent (src/lib/cookie-consent.ts,
// src/components/cookie-consent.tsx): Clarity sets first-party cookies as
// soon as its script runs, so this only loads it once "accepted" is on
// record, and listens for a same-session "accepted later" change instead of
// requiring a reload.
export function ClarityAnalytics() {
  useEffect(() => {
    const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID
    if (!projectId) return

    function load() {
      if (document.querySelector('script[data-clarity]')) return

      // Clarity's own tag script (https://www.clarity.ms/tag/<id>) assumes
      // window.clarity already exists as a queueing stub before it runs --
      // it checks window.clarity.v/.t as part of its own de-dup logic. Without
      // this stub defined first, that check crashes on undefined and Clarity
      // never initializes. This is Microsoft's own standard bootstrap snippet,
      // not something specific to this site.
      type ClarityFn = { (...args: unknown[]): void; q?: unknown[] }
      const w = window as unknown as { clarity?: ClarityFn }
      w.clarity =
        w.clarity ||
        ((...args: unknown[]) => {
          const fn = w.clarity as ClarityFn
          fn.q = fn.q || []
          fn.q.push(args)
        })

      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.clarity.ms/tag/${projectId}`
      script.dataset.clarity = 'true'
      document.head.appendChild(script)
    }

    if (getConsent() === 'accepted') {
      load()
      return
    }

    return onConsentChange((value) => {
      if (value === 'accepted') load()
    })
  }, [])

  return null
}
